import { productFilteringRequest } from "Vue/interfaces/ProductFilteringInterfaces";
import { wishlistRequest } from "Vue/interfaces/WishlistInterfaces";
import {
    LockDesignerAdminSearchRequest, LockDesignerAdminSearchResponse,
    LockDesignerAdminUpdateRequest, LockDesignerAdminUpdateResponse
} from "Vue/interfaces/LockDesignerAdminTool";
import {
    LockDesignerDoorTypeRequest,
    LockDesignerSelectionsRequest,
    LockDesignerDoorFitFinderRequest,
    LockDesignerFiltersRequest,
    LockDesignerProductsRequest,
    LockDesignerShareRequest,
    LockDesignerUserRegisterRequest,
    LockDesignerUserLoginRequest,
    LockDesignerUserForgotPasswordRequest,
    LockDesignerUserResetPasswordRequest,
    LockDesignerWishlistItemsRequest,
    LockDesignerWishlistItemsToAddRequest
} from "Vue/interfaces/LockDesigner";
import { useToast } from "vue-toastification";
const toast = useToast();

/**
 * This Function gets the Data for Showcaseses from an API Endpoint
 * @param data
 * @constructor
 */
export const GetShowcaseData = async (data) => {
    try {
        let url: any = new URL(`${window.location.protocol}//${window.location.host}/get-inspired/project-showcases/results`);
        Object.keys(data).forEach((key) => url.searchParams.append(key, data[key]));
        const response = await fetch(url);

        const rsp = await response.json();
        if (rsp.errorMessages?.length) {
            rsp.errorMessages.forEach((error) => {
                console.log(error.message);
            });
            return;
        } else {
            return rsp;
        }
    } catch (err) {
        return err;
    }
};

/**
 * This Function gets the Data for Support Videos from an API Endpoint
 * @param data
 * @constructor
 */
export const GetSupportVideosData = async (data) => {
    try {
        let url: any = new URL(`${window.location.protocol}//${window.location.host}/support/support-videos/results`);
        Object.keys(data).forEach((key) => url.searchParams.append(key, data[key]));

        const response = await fetch(url);
        const rsp = await response.json();

        if (rsp.errorMessages?.length) {
            rsp.errorMessages.forEach((error) => {
                console.log(error.message);
            });
            return;
        } else {
            return rsp;
        }
    } catch (err) {
        return err;
    }
};

/**
 * This function takes in a query paramater and hits and endpoint and returns back search results
 * @param query = The value of the search input
 * @constructor
 */
export const GetSearchData = async (query) => {
    return await ExecuteRequestAsync(`/api/bh/search?query=${query}`, 'GET', null, 'search request');
};

/**
 * Product filtering request
 * @param filterRequest Filter request
 * @returns
 */
export const FilterRequest = async (filterRequest: productFilteringRequest) => {
    return await ExecuteRequestAsync('/api/bh/product-filtering', 'POST', filterRequest, 'the product filtering request');
};

/**
 * Product family filtering request
 * @param familyRequest Family request
 * @returns
 */
export const FamilyRequest = async (familyRequest: any) => {
    return await ExecuteRequestAsync('/api/bh/product-family', 'POST', familyRequest, 'the product family endpoint');
};

/**
 * This function takes in product ids and hits and endpoint and returns back the products info *
 * @constructor
 */
export const GetProductData = async (wishlistRequest: wishlistRequest) => {
    return await ExecuteRequestAsync('/api/bh/product', 'POST', wishlistRequest, 'the checkout endpoint');
};

/**
 * Lock Designer Admin - Searches for products within the CMS
 * @param lockDesignerAdminSearchRequest Search request
 * @returns
 */
export const LockDesignerAdminSearch = async (lockDesignerAdminSearchRequest: LockDesignerAdminSearchRequest) => {
    return await ExecuteRequestAsync('/api/bh/admin/lock-designer', 'POST', lockDesignerAdminSearchRequest, 'Lock Designer Admin Tool search');
};

/**
 * Lock Designer Admin - Updates the x/y/scale information for a specific product
 * @param lockDesignerAdminUpdateRequest Update request
 * @returns
 */
export const LockDesignerAdminUpdate = async (lockDesignerAdminUpdateRequest: LockDesignerAdminUpdateRequest) => {
    return await ExecuteRequestAsync('/api/bh/admin/lock-designer', 'PUT', lockDesignerAdminUpdateRequest, 'Lock Designer Admin Tool update');
};

/**
 * Returns door type information used in tool to select Function, Door Prep, Door, etc.
 * @param lockDesignerDoorTypeRequest Door Type request`
 * @returns
 */
export const LockDesignerDoorType = async (lockDesignerDoorTypeRequest: LockDesignerDoorTypeRequest) => {
    return await ExecuteRequestAsync('/api/bh/lock-designer/door-type', 'POST', lockDesignerDoorTypeRequest, 'Lock Designer');
};

/**
 * Returns updated valid selections that are all compatible.
 * @param lockDesignerProductsRequest Current selections request
 * @returns
 */
export const LockDesignerProduct = async (lockDesignerProductsRequest: LockDesignerProductsRequest) => {
    return await ExecuteRequestAsync('/api/bh/lock-designer/products', 'POST', lockDesignerProductsRequest, 'Lock Designer');
};

/**
 * Returns updated valid selections that are all compatible.
 * @param lockDesignerDoorTypeRequest Current selections request
 * @returns
 */
export const LockDesignerSelections = async (lockDesignerSelectionsRequest: LockDesignerSelectionsRequest) => {
    return await ExecuteRequestAsync('/api/bh/lock-designer/selections', 'POST', lockDesignerSelectionsRequest, 'Lock Designer');
};

/**
 * Returns the updated function and door prep that corresponds to answered questions.
 * @param lockDesignerDoorFitFinderRequest Door Fit Finder request
 * @returns
 */
export const LockDesignerDoorFitFinder = async (lockDesignerDoorFitFinderRequest: LockDesignerDoorFitFinderRequest) => {
    return await ExecuteRequestAsync('/api/bh/lock-designer/door-fit-finder', 'POST', lockDesignerDoorFitFinderRequest, 'Lock Designer');
};

/**
 * Returns the filters that are currently available based on user's selections.
 * @param lockDesignerFiltersRequest Filters request
 * @returns
 */
export const LockDesignerFilters = async (lockDesignerFiltersRequest: LockDesignerFiltersRequest) => {
    return await ExecuteRequestAsync('/api/bh/lock-designer/filters', 'POST', lockDesignerFiltersRequest, 'Lock Designer');
};

/**
 * Returns the products for selected product type
 * @param lockDesignerProductsRequest Filters request
 * @returns
 */
export const LockDesignerProducts = async (lockDesignerProductsRequest: LockDesignerProductsRequest) => {
    return await ExecuteRequestAsync('/api/bh/lock-designer/products', 'POST', lockDesignerProductsRequest, 'Lock Designer');
};

/**
 * Sends an email to user with URL to current lock designer configuration
 * @param lockDesignerShareRequest Share request
 * @returns
 */
export const LockDesignerShare = async (lockDesignerShareRequest: LockDesignerShareRequest) => {
    return await ExecuteRequestAsync('/api/bh/lock-designer/share', 'POST', lockDesignerShareRequest, 'Lock Designer', true);
};

/**
 * Returns a PDF of user selections
 * @param lockDesignerSelectionsRequest User selections
 */
export const LockDesignerPDF = async (lockDesignerSelectionsRequest: LockDesignerSelectionsRequest) => {
    let requestUrl: any = new URL(`${window.location.protocol}//${window.location.host}/api/bh/lock-designer/pdf`);

    const fetchResponse = await fetch(requestUrl, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: lockDesignerSelectionsRequest ? JSON.stringify(lockDesignerSelectionsRequest) : null,
    });

    // Return PDF to browser
    const blob = await fetchResponse.blob();
    const file = window.URL.createObjectURL(blob);

    Object.assign(document.createElement('a'), {
        href: file,
        download: 'LockDesigner.pdf',
    }).click();
};

/**
 * Register new user as Lock Designer user
 * @param lockDesignerUserRegisterRequest Registration request
 */
export const UserRegister = async (lockDesignerUserRegisterRequest: LockDesignerUserRegisterRequest) => {
    return await ExecuteRequestAsync('/api/bh/lock-designer/register', 'POST', lockDesignerUserRegisterRequest, 'Lock Designer', true);
};

/**
 * Log in an existing Lock Designer user
 * @param lockDesignerUserLoginRequest Login request
 */
export const UserLogin = async (lockDesignerUserLoginRequest: LockDesignerUserLoginRequest) => {
    return await ExecuteRequestAsync('/api/bh/lock-designer/login', 'POST', lockDesignerUserLoginRequest, 'Lock Designer', true);
};

/**
 * Sends an email to user with link to reset their password
 * @param lockDesignerUserForgotPasswordRequest Forgot password request
 */
export const UserForgotPassword = async (lockDesignerUserForgotPasswordRequest: LockDesignerUserForgotPasswordRequest) => {
    return await ExecuteRequestAsync('/api/bh/lock-designer/forgot-password', 'POST', lockDesignerUserForgotPasswordRequest, 'Lock Designer', true);
};

/**
 * Resets login with new password
 * @param lockDesignerUserResetPasswordRequest Reset password request
 */
export const UserResetPassword = async (lockDesignerUserResetPasswordRequest: LockDesignerUserResetPasswordRequest) => {
    return await ExecuteRequestAsync('/api/bh/lock-designer/reset-password', 'POST', lockDesignerUserResetPasswordRequest, 'Lock Designer', true);
};

/**
 * Get list of Wishlist items to add, which will be listed in a modal
 * @param lockDesignerWishlistItemsToAddRequest Current selections
 * @param authToken Authorization token
 * @returns
 */
export const WishlistItemsToAdd = async (lockDesignerWishlistItemsToAddRequest: LockDesignerWishlistItemsToAddRequest, authToken: string) => {
    return await ExecuteRequestAsync('/api/bh/lock-designer/wishlist-items-to-add', 'POST', lockDesignerWishlistItemsToAddRequest, 'Lock Designer', true, authToken);
};

/**
 * Adds Wishlist items to Wishlist
 * @param lockDesignerWishlistItemsToAddRequest Current selections
 * @param authToken Authorization token
 * @returns
 */
export const WishlistItemsAdd = async (lockDesignerWishlistItemsToAddRequest: LockDesignerWishlistItemsToAddRequest, authToken: string) => {
    return await ExecuteRequestAsync('/api/bh/lock-designer/wishlist-items-add', 'POST', lockDesignerWishlistItemsToAddRequest, 'Lock Designer', true, authToken);
};

/**
 * Gets Wishlist items for selected wishlist name. If wishlist name isn't populated, then it returns the items for the
 * first wishlist item name (alphabetical)
 * @param lockDesignerWishlistItemsRequest
 * @param authToken Authorization token
 * @returns
 */
export const WishlistItems = async (lockDesignerWishlistItemsRequest: LockDesignerWishlistItemsRequest, authToken: string) => {
    return await ExecuteRequestAsync('/api/bh/lock-designer/wishlist-items', 'POST', lockDesignerWishlistItemsRequest, 'Lock Designer', true, authToken);
};

/**
 * Deletes an item from the user's wishlist
 * @param id ID of item being deleted
 * @param authToken Authorization token
 * @returns
 */
export const WishlistItemDelete = async (id: number, authToken: string) => {
    return await ExecuteRequestAsync(`/api/bh/lock-designer/wishlist-items/${id}`, 'DELETE', null, 'Lock Designer', true, authToken);
};

/**
 * Submits request to API and handles response
 * @param url API URL
 * @param method Request method (GET, POST, PUT etc.)
 * @param payload Request payload
 * @param errorMessage Error message string
 * @param fullResponse Whether to return full responseObject
 * @param authToken
 * @returns
 */
const ExecuteRequestAsync = async (url: string, method: string, payload: any, errorMessage: string, fullResponse = false, authToken = null) => {
    try {
        let requestUrl: any = new URL(`${window.location.protocol}//${window.location.host}${url}`);
        const fetchResponse = await fetch(requestUrl, {
            method: method,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: authToken ? `Bearer ${authToken}` : null
            },
            body: payload ? JSON.stringify(payload) : null,
        });

        const response = await fetchResponse.json();

        if (!fullResponse && response.errorDetail?.isError) {
            response.errorDetail?.messages.forEach((error) => {
                toast.error(error, {
                    timeout: 5000,
                });
            });
            return;
        }

        if (fullResponse) {
            return response;
        } else {
            return response.data;
        }
    } catch (err) {
        toast.error(`There was a problem with ${errorMessage}: ${err}`, {
            timeout: 5000,
        });
        return err;
    }
};
